import { ChakraFlex, ChakraSimpleGrid, FAIconName } from '@equip.health/ui';
import type { GroupDetails } from '~/lib/util/groups.utils';

import { GroupClassAgeRangeField } from '~/components/maud/calendar/group-class/GroupClassAgeRangeField';
import { GroupClassAttendeeTypesField } from '~/components/maud/calendar/group-class/GroupClassAttendeeTypesField';
import { GroupClassDiagnosisField } from '~/components/maud/calendar/group-class/GroupClassDiagnosisField';
import { GroupClassModalitiesField } from '~/components/maud/calendar/group-class/GroupClassModalitiesField';
import { GroupClassTreatmentProgramsField } from '~/components/maud/calendar/group-class/GroupClassTreatmentProgramsField';
import { GroupTypesField } from '~/components/maud/calendar/group-class/GroupTypesField';

interface RegistrationCriteriaProps {
  formState: GroupDetails;
  updateFormState?: (field: keyof GroupDetails, value: unknown) => void;
  isReadOnly?: boolean;
  placeholder?: string;
}

const RegistrationCriteria = ({
  formState,
  updateFormState,
  isReadOnly,
  placeholder,
}: RegistrationCriteriaProps) => {
  const flexWrapStyle = {
    '& *': {
      whiteSpace: 'normal !important',
      wordBreak: 'break-word',
    },
  };

  const hasValue = (key: keyof GroupDetails): boolean =>
    Array.isArray(formState?.[key]) &&
    (formState[key] as Array<unknown>).length > 0;

  const iconConfig: Record<string, FAIconName> = {
    attendeeRoles: FAIconName.userGroupSolid,
    treatmentPrograms: FAIconName.folderMedical,
    treatmentModalities: FAIconName.bookMedicalRegular,
    diagnoses: FAIconName.stethoscopeRegular,
    ageRanges: FAIconName.calendarRangeRegular,
  };

  const getFieldIcon = (key: keyof GroupDetails): FAIconName | undefined =>
    hasValue(key) ? iconConfig[key as string] : undefined;

  return (
    <ChakraSimpleGrid columns={{ base: 1, md: 2 }} gap="24px" rowGap="18px">
      {!isReadOnly && (
        <GroupTypesField
          isReadOnly={isReadOnly}
          onChange={(value) => updateFormState?.('groupTypeCode', value)}
          placeholder={placeholder}
          value={formState?.groupTypeCode}
        />
      )}
      <GroupClassAttendeeTypesField
        isReadOnly={isReadOnly}
        leftIcon={getFieldIcon('attendeeRoles')}
        onChange={(value) => updateFormState?.('attendeeRoles', value)}
        placeholder={placeholder}
        value={formState?.attendeeRoles}
      />
      <ChakraFlex sx={flexWrapStyle}>
        <GroupClassTreatmentProgramsField
          isReadOnly={isReadOnly}
          leftIcon={getFieldIcon('treatmentPrograms')}
          onChange={(value) => updateFormState?.('treatmentPrograms', value)}
          placeholder={placeholder}
          value={formState?.treatmentPrograms}
        />
      </ChakraFlex>

      <GroupClassModalitiesField
        onChange={(value) => updateFormState?.('treatmentModalities', value)}
        value={formState?.treatmentModalities}
        leftIcon={getFieldIcon('treatmentModalities')}
        isReadOnly={isReadOnly}
        placeholder={placeholder}
      />
      <ChakraFlex sx={flexWrapStyle}>
        <GroupClassDiagnosisField
          isReadOnly={isReadOnly}
          leftIcon={getFieldIcon('diagnoses')}
          onChange={(value) => updateFormState?.('diagnoses', value)}
          placeholder={placeholder}
          value={
            isReadOnly
              ? formState?.diagnoses?.map((item) => item.replace('_', '-'))
              : formState?.diagnoses
          }
        />
      </ChakraFlex>

      <GroupClassAgeRangeField
        isReadOnly={isReadOnly}
        leftIcon={getFieldIcon('ageRanges')}
        onChange={(value) => updateFormState?.('ageRanges', value)}
        placeholder={placeholder}
        value={formState?.ageRanges}
      />
    </ChakraSimpleGrid>
  );
};

export default RegistrationCriteria;
